import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input, type InputProps } from './input';

/**
 * A password field with a show/hide toggle. Every prop but `type` reaches the `<input>`, so a
 * `<label htmlFor>` or a FormControl names the field itself; the toggle is a separate button named
 * "Show password" whose `aria-pressed` says whether the text is shown.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, Omit<InputProps, 'type'>>(
  ({ className, disabled, ...props }, ref) => {
    const { t } = useTranslation();
    const [shown, setShown] = React.useState(false);
    const Icon = shown ? EyeOff : Eye;
    return (
      <div className="relative">
        <Input
          ref={ref}
          {...props}
          type={shown ? 'text' : 'password'}
          disabled={disabled}
          className={cn('pr-10', className)}
        />
        <button
          type="button"
          aria-label={t('common.showPassword')}
          aria-pressed={shown}
          disabled={disabled}
          onClick={() => setShown((v) => !v)}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icon className="h-4 w-4" aria-hidden />
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
